import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, ChevronLeft, ChevronRight, Heart, MessageCircle, Share, Bookmark, MoreHorizontal, User, Clock, Eye } from 'lucide-react';
import Modal from './Modal';
import './PreviewPopup.css';

interface PreviewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: 'story' | 'photo' | 'video';
  data: PreviewData;
}

interface PreviewData {
  // Common fields
  description: string;
  privacy: 'public' | 'friends' | 'private';
  location?: string;
  
  // Media content
  photos?: Array<{
    id: string;
    preview: string;
    description?: string;
    audioData?: any;
  }>;
  
  video?: {
    preview: string;
    duration?: number;
    trimStart?: number;
    trimEnd?: number;
    audioData?: any;
  };
  
  story?: {
    preview: string;
    type: 'photo' | 'video';
    duration?: number;
    audioData?: any;
  };
}

const PreviewPopup: React.FC<PreviewPopupProps> = ({ isOpen, onClose, onConfirm, type, data }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Auto-hide controls for stories
  useEffect(() => {
    if (type === 'story' && showControls) {
      timeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [type, showControls]);

  // Video event handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Photo navigation
  const handlePrevPhoto = () => {
    if (data.photos) {
      setCurrentPhotoIndex(prev => Math.max(0, prev - 1));
    }
  };

  const handleNextPhoto = () => {
    if (data.photos) {
      setCurrentPhotoIndex(prev => Math.min(data.photos!.length - 1, prev + 1));
    }
  };

  // Format time for display
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Get privacy icon
  const getPrivacyIcon = () => {
    switch (data.privacy) {
      case 'private':
        return '🔒';
      case 'friends':
        return '👥';
      default:
        return '🌍';
    }
  };

  // Render story preview
  const renderStoryPreview = () => {
    if (!data.story) return null;

    return (
      <div className="story-preview-container">
        {/* Story Header */}
        <div className={`story-header ${showControls ? 'visible' : 'hidden'}`}>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">Your Story</p>
              <p className="text-white/70 text-xs flex items-center">
                <Clock size={12} className="mr-1" />
                Preview
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-white/70 text-xs">{getPrivacyIcon()}</span>
            <MoreHorizontal size={20} className="text-white" />
          </div>
        </div>

        {/* Story Content */}
        <div 
          className="story-content"
          onClick={() => setShowControls(!showControls)}
        >
          {data.story.type === 'video' ? (
            <video
              ref={videoRef}
              src={data.story.preview}
              className="story-media"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              muted={isMuted}
              playsInline
            />
          ) : (
            <img 
              src={data.story.preview} 
              alt="Story preview"
              className="story-media"
            />
          )}

          {/* Story Controls */}
          {data.story.type === 'video' && showControls && (
            <div className="story-controls">
              <button onClick={handlePlayPause} className="play-btn">
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <button onClick={handleMuteToggle} className="mute-btn">
                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
              </button>
            </div>
          )}
        </div>

        {/* Story Footer */}
        {data.description && (
          <div className={`story-footer ${showControls ? 'visible' : 'hidden'}`}>
            <p className="text-white text-sm">{data.description}</p>
            {data.location && (
              <p className="text-white/70 text-xs mt-1">📍 {data.location}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render photo preview
  const renderPhotoPreview = () => {
    if (!data.photos || data.photos.length === 0) return null;

    const currentPhoto = data.photos[currentPhotoIndex];

    return (
      <div className="photo-preview-container">
        {/* Post Header */}
        <div className="post-header">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
              <User size={20} className="text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Your Post</p>
              <p className="text-gray-500 text-sm flex items-center">
                <Clock size={12} className="mr-1" />
                Preview • {getPrivacyIcon()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="text-blue-600 text-sm font-medium">Follow</button>
            <MoreHorizontal size={20} className="text-gray-400" />
          </div>
        </div>

        {/* Photo Content */}
        <div className="photo-content">
          <div className="relative">
            <img 
              src={currentPhoto.preview} 
              alt={`Photo ${currentPhotoIndex + 1}`}
              className="post-image"
            />
            
            {/* Photo Navigation */}
            {data.photos.length > 1 && (
              <>
                <button 
                  onClick={handlePrevPhoto}
                  disabled={currentPhotoIndex === 0}
                  className="nav-arrow left"
                >
                  <ChevronLeft size={20} />
                </button>
                <button 
                  onClick={handleNextPhoto}
                  disabled={currentPhotoIndex === data.photos.length - 1}
                  className="nav-arrow right"
                >
                  <ChevronRight size={20} />
                </button>
                
                {/* Photo Indicators */}
                <div className="photo-indicators">
                  {data.photos.map((_, index) => (
                    <div
                      key={index}
                      className={`indicator ${index === currentPhotoIndex ? 'active' : ''}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Post Stats */}
        <div className="post-stats">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="flex items-center text-gray-600">
                <Eye size={16} className="mr-1" />
                0 views
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600">Preview</span>
            </div>
            <span className="text-gray-600">→</span>
          </div>
        </div>

        {/* Post Actions */}
        <div className="post-actions">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button className="action-btn">
                <Heart size={20} />
              </button>
              <button className="action-btn">
                <MessageCircle size={20} />
              </button>
              <button className="action-btn">
                <Share size={20} />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-gray-400">🎁</button>
              <button className="action-btn">
                <Bookmark size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Post Caption */}
        {data.description && (
          <div className="post-caption">
            <p className="text-gray-900">
              <span className="font-medium">You</span> {data.description}
            </p>
            {data.location && (
              <p className="text-gray-500 text-sm mt-1">📍 {data.location}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render video preview
  const renderVideoPreview = () => {
    if (!data.video) return null;

    return (
      <div className="video-preview-container">
        {/* Post Header */}
        <div className="post-header">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
              <User size={20} className="text-white" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Your Video</p>
              <p className="text-gray-500 text-sm flex items-center">
                <Clock size={12} className="mr-1" />
                Preview • {getPrivacyIcon()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button className="text-blue-600 text-sm font-medium">Follow</button>
            <MoreHorizontal size={20} className="text-gray-400" />
          </div>
        </div>

        {/* Video Content */}
        <div className="video-content">
          <div className="relative">
            <video
              ref={videoRef}
              src={data.video.preview}
              className="post-video"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
              muted={isMuted}
              playsInline
            />
            
            {/* Video Controls */}
            <div className="video-controls">
              <button onClick={handlePlayPause} className="play-btn-large">
                {isPlaying ? <Pause size={32} /> : <Play size={32} />}
              </button>
              
              <div className="controls-bottom">
                <div className="time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
                <button onClick={handleMuteToggle} className="mute-btn">
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Post Stats */}
        <div className="post-stats">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="flex items-center text-gray-600">
                <Eye size={16} className="mr-1" />
                0 views
              </span>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600">Preview</span>
            </div>
            <span className="text-gray-600">→</span>
          </div>
        </div>

        {/* Post Actions */}
        <div className="post-actions">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button className="action-btn">
                <Heart size={20} />
              </button>
              <button className="action-btn">
                <MessageCircle size={20} />
              </button>
              <button className="action-btn">
                <Share size={20} />
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-gray-400">🎁</button>
              <button className="action-btn">
                <Bookmark size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Post Caption */}
        {data.description && (
          <div className="post-caption">
            <p className="text-gray-900">
              <span className="font-medium">You</span> {data.description}
            </p>
            {data.location && (
              <p className="text-gray-500 text-sm mt-1">📍 {data.location}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const getTitle = () => {
    switch (type) {
      case 'story':
        return 'Story Preview';
      case 'photo':
        return 'Photo Post Preview';
      case 'video':
        return 'Video Post Preview';
      default:
        return 'Preview';
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={getTitle()} 
      size={type === 'story' ? 'sm' : 'lg'}
    >
      <div className="preview-content">
        {type === 'story' && renderStoryPreview()}
        {type === 'photo' && renderPhotoPreview()}
        {type === 'video' && renderVideoPreview()}
      </div>

      {/* Action Buttons */}
      <div className="preview-actions">
        <button
          onClick={onClose}
          className="btn-secondary"
        >
          Edit More
        </button>
        <button
          onClick={onConfirm}
          className="btn-primary"
        >
          {type === 'story' ? 'Share Story' : 'Publish Post'}
        </button>
      </div>
    </Modal>
  );
};

export default PreviewPopup;
